import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { FormUtility } from '../../../shared/forms/form-utility';
import { FormValidation } from '../../../shared/forms/form-validation';
import { ClaimCustomerValidator } from '../../../shared/custom-validators/claimCustomerValidator';

@Component({
  selector: 'claims-app-customer',
  templateUrl: './claims-app-customer.component.html',
})
export class ClaimsAppCustomerComponent implements OnInit {
  @ViewChild('contractButton') contractNameButtonRef!: ElementRef;
  @ViewChild('customerNameButton') customerNameButtonRef!: ElementRef;

  public form: UntypedFormGroup;
  public formCompleted = false;

  public mbiWarningDialog: MatDialogRef<DialogComponent>;
  private readonly mbiPrefixes = ["MBI", "MBIQ", "MBC"];

  public readonly minCurrentMileage = 0;
  public readonly maxCurrentMileage = 9999999;

  public requireContractNumber = false;
  public requireCustomerName = false;
  public optionalCustomerName = false;
  public customerIdType = {
    CONTRACTNUMBER: 'contract',
    CUSTOMERNAME: 'name'
  };

  constructor(private dialog: MatDialog, private claimCustomerValidator: ClaimCustomerValidator) { }

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      'vin': new UntypedFormControl("", [Validators.required, Validators.pattern(/^[A-Za-z0-9]{6}$/)]),
      'contractNumber': new UntypedFormControl("", [Validators.minLength(7), Validators.maxLength(25), Validators.required]),
      'customerFirstName': new UntypedFormControl("", [Validators.maxLength(60), Validators.required]),
      'customerLastName': new UntypedFormControl("", [Validators.maxLength(60), Validators.required])
    });
  }

  /**
   * Formats the contract number input by removing non-alphanumeric characters and capitalizing.
   */
  public onContractNumberInput(): void {
    const rawValue = this.form.controls['contractNumber']?.value;

    // Remove all non-alphanumeric characters and capitalize
    if (rawValue) {
      const formattedValue = rawValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      this.form.controls['contractNumber'].setValue(formattedValue);
    }
    this.validateForm();
  }

  /*
   * Enforces contractNumber validation rules when field focus is lost.
   */
  public onContractNumberBlur(): void {
    const controlValue = this.form.controls['contractNumber']?.value;
    if (this.mbiPrefixes.some(p => String(controlValue)?.trim().startsWith(p))) {
      this.showMbiWarningDialog();
    }
    this.validateForm();
  }

  /**
   * Formats the VIN input by removing non-alphanumeric characters and capitalizing.
   * Handles pasted content by removing spaces before applying maxlength restriction.
   */
  public onVinInput(): void {
    const input = this.form.controls['vin']?.value;
    if (input) {
      const formattedValue = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
      this.form.controls['vin'].setValue(formattedValue);
    }
    this.validateForm();
  }

  /**
   * Trims leading and trailing spaces from name input fields when focus is lost.
   * @param fieldName The name of the form control to update
   */
  public onNameBlur(fieldName: string): void {
    const control = this.form.controls[fieldName];
    if (control.value) {
      control.setValue(control.value.trim());
    }
    this.validateForm();
  }

  public onNameInput(): void {
    this.validateForm();
  }

  //** Validates the customer form and sets formCompleted to valid or invalid */
  public validateForm(): void {
    this.formCompleted = FormValidation.isValid(this.form);
    this.optionalCustomerName = this.requireContractNumber ? true : false;
  }


  /**
   * Resets the form.
   * @see {@link FormUtility.reset}
   */
  public reset(): void {
    FormUtility.reset(this.form);
    this.formCompleted = false;
  }

  /**
   * Clears the value in the specified form field.
   * @param field The form field to clear.
   */
  public clear(field: string): void {
    FormUtility.clear(this.form, field);
    this.validateForm();
  }

  /**
   * Gets a value indicating if the specified field has a value.
   * @param field The form field to check.
   * @returns If the field has a value, `true`; otherwise, `false`.
   */
  public hasValue(field: string): boolean {
    return FormUtility.hasValue(this.form, field)
  }

  public showMbiWarningDialog() {
    this.mbiWarningDialog = this.dialog.open(DialogComponent, {
      data: {
        title: "Claim cannot be submitted online",
        bodyText: "We appreciate your business, unfortunately your claim cannot be submitted online. To proceed with your claim, please contact our support team at 1-800-531-1925 for further assistance. Our representatives are standing by to help you complete your submission.",
        confirmationButtonText: "Close"
      }
    });

    this.mbiWarningDialog.afterClosed().subscribe(_ => {
      // Result does not matter. Always clear the contract number.
      this.form.controls['contractNumber'].setValue('');
    });
  }

  public shouldShowStep(stepNumber: number): boolean {
    switch (stepNumber) {
      case 1:
        return true;
      case 2:
        return this.form.controls['vin'].valid && this.form.controls['vin'].value ? true : false;
      case 3:
        return ((this.form.controls['vin'].valid && this.form.controls['vin'].value) && (this.requireContractNumber || this.requireCustomerName)) ? true : false;
      default:
        return false;
    }
  }

  public toggleCustomerIdentification(customerIdentificationType: string): void {
    switch (customerIdentificationType) {
      case this.customerIdType.CONTRACTNUMBER:
        this.requireContractNumber = true;
        this.requireCustomerName = false;
        this.claimCustomerValidator.removeRequiredValidators([this.form.controls['customerFirstName'], this.form.controls['customerLastName']]);
        this.claimCustomerValidator.addRequiredValidator(this.form.controls['contractNumber']);        
        break;
      case this.customerIdType.CUSTOMERNAME:
        this.requireCustomerName = true;
        this.requireContractNumber = false;
        this.claimCustomerValidator.removeRequiredValidator(this.form.controls['contractNumber']);
        this.claimCustomerValidator.addRequiredValidators([this.form.controls['customerFirstName'], this.form.controls['customerLastName']]);
        this.clear('contractNumber');
        break;
      default:
        this.requireContractNumber = false;
        this.requireCustomerName = false;
        this.claimCustomerValidator.removeRequiredValidator(this.form.controls['contractNumber']);
        this.claimCustomerValidator.removeRequiredValidators([this.form.controls['customerFirstName'], this.form.controls['customerLastName']]);
    }
    this.validateForm();
  }

}
